import { useStore } from '@tanstack/react-form'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router'
import Card from 'src/components/containers/card'
import Label from 'src/components/fields/label'
import ListboxField from 'src/components/fields/listbox.field'
import TextField from 'src/components/fields/text.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import Alert from 'src/components/messages/alert'
import Tooltip from 'src/components/messages/tooltip'
import { BasicTable } from 'src/components/tables/basic-table'
import {
  parseCsvFile,
  validateColumnTypeCompatibility,
  validateDateTimeFormatCompatibility,
} from 'src/utils/csv-parser'
import notify from 'src/utils/notify'

import { StepId } from '../config'

const CSV_OPTIONS = [
  {
    name: 'header',
    label: 'Header',
    help: 'Specify if the CSV has a header row.',
    options: [
      { label: 'First line', value: 'first_line' },
      { label: 'No header', value: 'no_header' },
    ],
  },
  {
    name: 'delimiter',
    label: 'Delimiter',
    help: 'Specify the separator for values.',
    options: [
      { label: 'Comma', value: ',' },
      { label: 'Semicolon', value: ';' },
      { label: 'Pipe', value: '|' },
    ],
  },
  {
    name: 'whitespace',
    label: 'Whitespace',
    help: 'Trim or preserve whitespace from the beginning and end of values.',
    options: [
      { label: 'Trim', value: 'trim' },
      { label: 'Preserve', value: 'preserve' },
    ],
  },
  {
    name: 'enclosure',
    label: 'Optional field enclosure',
    help: 'Specify if some values are wrapped in single or double-quotes.',
    options: [
      { label: 'Double quotes', value: '"' },
      { label: 'Single quotes', value: "'" },
      { label: 'None', value: '' },
    ],
  },
  {
    name: 'dateFormat',
    label: 'Date format',
    help: 'Specify date formats for autodetection.',
    options: [
      { label: 'Auto', value: 'auto' },
      { label: 'YYYY-MM-DD', value: 'yyyy-mm-dd' },
      { label: 'MM/DD/YYYY', value: 'mm/dd/yyyy' },
    ],
  },
  {
    name: 'timeFormat',
    label: 'Time format',
    help: 'Specify time formats for autodetection.',
    options: [
      { label: 'Auto', value: 'auto' },
      { label: 'HH:mm:ss', value: 'hh:mm:ss' },
      { label: 'HH:mm', value: 'hh:mm' },
    ],
  },
  {
    name: 'timestampFormat',
    label: 'Timestamp format',
    help: 'Specify timestamp formats for autodetection.',
    options: [
      { label: 'Auto', value: 'auto' },
      { label: 'ISO 8601', value: 'iso8601' },
    ],
  },
  {
    name: 'errorHandling',
    label: 'Error handling',
    help: 'Specify error handling actions.',
    options: [
      { label: 'Stop', value: 'stop' },
      { label: 'Skip row', value: 'skip' },
    ],
  },
]

const TYPE_OPTIONS = [
  { label: 'FLOAT', value: 'FLOAT' },
  { label: 'STRING', value: 'VARCHAR' },
  { label: 'BINARY', value: 'BINARY' },
  { label: 'BOOLEAN', value: 'BOOLEAN' },
  { label: 'DATE', value: 'DATE' },
  { label: 'TIME', value: 'TIME' },
  { label: 'TIMESTAMP', value: 'TIMESTAMP' },
  { label: 'INT', value: 'INT' },
  { label: 'LONG', value: 'LONG' },
]

const validateSqlColumnName = (value: string) => {
  if (!/^[_a-zA-Z][_a-zA-Z0-9]*$/.test(value)) {
    return 'Must be SQL compliant'
  }
}

const hasDuplicates = (arr: string[]): boolean => new Set(arr).size !== arr?.length

export const StepConfigureCsv: StepComponent = ({ context }) => {
  const form = context.form
  const navigate = useNavigate()
  const [isReprocessing, setIsReprocessing] = useState(false)
  const [isProcessingNext, setIsProcessingNext] = useState(false)
  const [isInitializing, setIsInitializing] = useState(true)
  const [validationErrors, setValidationErrors] = useState<{
    columnTypeErrors: number[]
    dateFormatErrors: boolean
    dateFormatErrorColumns: number[]
  }>({
    columnTypeErrors: [],
    dateFormatErrors: false,
    dateFormatErrorColumns: [],
  })

  const { parsedCsvData, guessedColumnTypes, hasHeader, csvFile, csvOptions, columnTypes } = useStore(
    context.form.store,
    (state) => state.values,
  )

  // Defer initialization to prevent page blocking
  useEffect(() => {
    if (guessedColumnTypes && guessedColumnTypes.length > 0 && isInitializing) {
      // Use requestAnimationFrame to defer heavy work
      requestAnimationFrame(() => {
        setIsInitializing(false)
      })
    }
  }, [guessedColumnTypes, isInitializing])

  // Optimized validation - only check what's needed
  const validateColumnName = useCallback(
    ({ value }: { value: string }) => {
      // Immediate SQL compliance check
      const sqlError = validateSqlColumnName(value)
      if (sqlError) return sqlError

      // Check for duplicates on blur
      const columnNames = form.getFieldValue('columnNames') || []
      const count = columnNames.filter((name: string) => name === value).length
      return count > 1 ? 'Must be unique' : undefined
    },
    [form],
  )

  // Re-parse CSV when options change
  useEffect(() => {
    if (!csvFile || !csvOptions || isReprocessing) return

    // Skip re-parsing for error handling option changes
    if (
      form.getFieldValue('_lastProcessedOptions') &&
      Object.entries(csvOptions).every(
        ([key, value]) => key === 'errorHandling' || value === form.getFieldValue('_lastProcessedOptions')[key],
      )
    ) {
      return
    }

    setIsReprocessing(true)

    // Preserve user's manual column type and name selections
    const currentColumnNames = form.getFieldValue('columnNames')
    const currentColumnTypes = form.getFieldValue('columnTypes')

    parseCsvFile(csvFile, csvOptions)
      .then(({ data, fields, hasHeader, guessedColumnTypes }) => {
        form.setFieldValue('parsedCsvData', data)
        form.setFieldValue('parsedCsvFields', fields)
        form.setFieldValue('guessedColumnTypes', guessedColumnTypes)
        form.setFieldValue('hasHeader', hasHeader)
        form.setFieldValue('_lastProcessedOptions', { ...csvOptions })

        // Only reset column names and types if they don't exist yet (initial load)
        // Otherwise preserve user selections
        if (!currentColumnNames || !currentColumnTypes) {
          // Set values together to minimize re-renders
          const newColumnNames = guessedColumnTypes.map((col) => col.name)
          const newColumnTypes = guessedColumnTypes.map((col) => col.type)

          // Use setTimeout to batch DOM updates
          setTimeout(() => {
            form.setFieldValue('columnNames', newColumnNames)
            form.setFieldValue('columnTypes', newColumnTypes)
          }, 0)
        }

        setIsReprocessing(false)
      })
      .catch((err) => {
        notify.error(`Error parsing CSV with new options: ${err instanceof Error ? err.message : err}`)
        setIsReprocessing(false)
      })
  }, [csvOptions, csvFile, form])

  const checkFormatError = (
    formatType: 'dateFormat' | 'timeFormat' | 'timestampFormat',
    formatValue: string,
  ): boolean => {
    if (!parsedCsvData || !guessedColumnTypes || !columnTypes || formatValue === 'auto') return false

    const targetColumnType = formatType === 'dateFormat' ? 'DATE' : formatType === 'timeFormat' ? 'TIME' : 'TIMESTAMP'

    return guessedColumnTypes.some((col: any, idx: number) => {
      const selectedType = columnTypes[idx]
      if (selectedType !== targetColumnType) return false

      const columnValues = parsedCsvData.map((row: any) => row[col.name])
      const tempOptions = { ...csvOptions, [formatType]: formatValue }
      return validateDateTimeFormatCompatibility(columnValues, selectedType, tempOptions)
    })
  }

  // Skip initial validation to speed up load time
  // Validation will happen on blur or before submission
  useEffect(() => {
    if (!parsedCsvData || !guessedColumnTypes || !columnTypes) return

    const columnTypeErrors: number[] = []
    const dateFormatErrorColumns: number[] = []

    // Only validate first 20 columns + every 10th column after that
    const columnsToCheck = new Set<number>()
    for (let i = 0; i < Math.min(20, guessedColumnTypes.length); i++) {
      columnsToCheck.add(i)
    }
    for (let i = 20; i < guessedColumnTypes.length; i += 10) {
      columnsToCheck.add(i)
    }

    // Sample only 10 rows for validation
    const sampleSize = Math.min(10, parsedCsvData.length)
    const sampleData = parsedCsvData.slice(0, sampleSize)

    columnsToCheck.forEach((idx) => {
      const col = guessedColumnTypes[idx]
      const columnValues = sampleData.map((row: any) => row[col.name])
      const selectedType = columnTypes[idx]

      if (!validateColumnTypeCompatibility(columnValues, selectedType)) {
        columnTypeErrors.push(idx)
      }

      if (validateDateTimeFormatCompatibility(columnValues, selectedType, csvOptions)) {
        dateFormatErrorColumns.push(idx)
      }
    })

    setValidationErrors({
      columnTypeErrors,
      dateFormatErrors: dateFormatErrorColumns.length > 0,
      dateFormatErrorColumns,
    })
  }, [parsedCsvData, guessedColumnTypes, columnTypes, csvOptions])

  // Simplified column type validation
  const validateColumnType = useCallback(
    (idx: number) => {
      return ({ value }: { value: string }) => {
        if (!parsedCsvData || !guessedColumnTypes || !csvOptions) return

        const col = guessedColumnTypes[idx]
        if (!col) return

        // Only validate first 5 rows on blur for performance
        const sampleSize = Math.min(5, parsedCsvData.length)
        const columnValues = parsedCsvData.slice(0, sampleSize).map((row: any) => row[col.name])

        if (!validateColumnTypeCompatibility(columnValues, value)) {
          return ' '
        }

        if (validateDateTimeFormatCompatibility(columnValues, value, csvOptions)) {
          return ' '
        }
      }
    },
    [parsedCsvData, guessedColumnTypes, csvOptions],
  )

  const validateDateFormat = ({ value }: { value: string }) => {
    return checkFormatError('dateFormat', value) ? ' ' : undefined
  }

  const validateTimeFormat = ({ value }: { value: string }) => {
    return checkFormatError('timeFormat', value) ? ' ' : undefined
  }

  const validateTimestampFormat = ({ value }: { value: string }) => {
    return checkFormatError('timestampFormat', value) ? ' ' : undefined
  }

  const hasErrors = validationErrors.columnTypeErrors.length > 0 || validationErrors.dateFormatErrors

  const getAlertMessage = () => {
    const hasColumnTypeIssue = validationErrors.columnTypeErrors.length > 0
    const hasDateFormatIssue = validationErrors.dateFormatErrors
    const errorHandling = csvOptions?.errorHandling || 'stop'
    const errorStrategy =
      errorHandling === 'stop'
        ? 'These values will cause the ingestion to stop'
        : 'These values will cause the ingestion to skip rows'

    if (hasColumnTypeIssue && hasDateFormatIssue) {
      return (
        <>
          <strong>Ensure that all column types and date/time formats are correct.</strong> Some sample values do not
          match the selected column types and date/time formats. {errorStrategy}
        </>
      )
    } else if (hasDateFormatIssue) {
      return (
        <>
          <strong>Ensure that all date/time formats are correct.</strong> Some sample values do not match the selected
          date/time formats. {errorStrategy}
        </>
      )
    } else {
      return (
        <>
          <strong>Ensure that all column types are correct.</strong> Some sample values do not match the selected column
          types. {errorStrategy}
        </>
      )
    }
  }

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Configure CSV'
        description='Configure your CSV by verifying general options as well as data types and names for each column'
      />
      <ScrollableStepContent inset autoHeight>
        <div className='mx-auto w-full max-w-[1440px] px-12'>
          {hasErrors && (
            <div className='mb-6'>
              <Alert kind='danger'>{getAlertMessage()}</Alert>
            </div>
          )}
          <div className='flex items-start gap-8'>
            <Card padding='xl' className='text-sm'>
              <div>
                <div className='mb-8 text-lg font-bold'>CSV Options</div>
                <div className='grid gap-4'>
                  {CSV_OPTIONS.map((opt) => (
                    <div key={opt.name} className='flex items-center gap-4'>
                      <div className='flex w-48 items-center justify-between'>
                        <Label name={`csvOptions.${opt.name}`} label={opt.label} className='!mb-0 text-sm' />
                        {opt.help && <Tooltip>{opt.help}</Tooltip>}
                      </div>
                      <div className='w-56 flex-1'>
                        <ListboxField
                          name={`csvOptions.${opt.name}`}
                          options={opt.options}
                          form={form}
                          id={`csvOptions.${opt.name}`}
                          defaultValue={
                            opt.name === 'header' ? (hasHeader ? 'first_line' : 'no_header') : opt.options[0]?.value
                          }
                          wrapperClassName='!mb-0'
                          validators={
                            opt.name === 'dateFormat'
                              ? { onChange: validateDateFormat }
                              : opt.name === 'timeFormat'
                                ? { onChange: validateTimeFormat }
                                : opt.name === 'timestampFormat'
                                  ? { onChange: validateTimestampFormat }
                                  : undefined
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
            <div className='relative flex-1'>
              {isInitializing ? (
                <div className='flex items-center justify-center p-12'>
                  <div className='text-center'>
                    <div className='mb-4 text-lg font-semibold'>Loading columns...</div>
                    <div className='text-gray-600'>Setting up fields for {guessedColumnTypes?.length || 0} columns</div>
                  </div>
                </div>
              ) : (
                <>
                  <BasicTable
                    tableClassName='align-top table-fixed'
                    columnHeaders={['Column Name', 'Type', 'Sample Value']}
                    rows={
                      guessedColumnTypes?.map((col: any, idx: number) => [
                        <TextField
                          key={col.name}
                          name={`columnNames.${idx}`}
                          form={form}
                          defaultValue={col.name}
                          wrapperClassName='!mb-0'
                          validators={{
                            onBlur: validateColumnName,
                          }}
                        />,
                        <ListboxField
                          key={col.name}
                          name={`columnTypes.${idx}`}
                          options={TYPE_OPTIONS}
                          form={form}
                          wrapperClassName='!mb-0'
                          defaultValue={col.type}
                          validators={{
                            onBlur: validateColumnType(idx),
                          }}
                        />,
                        <div className='truncate py-3' key={col.name}>
                          {parsedCsvData && parsedCsvData[0] ? parsedCsvData[0][col.name] ?? '' : ''}
                        </div>,
                      ]) || []
                    }
                  />

                  {/* Loading overlay when processing next. Since we clear out some of the config to avoid 
                  performance issues, the UI briefly wipes out the displayed columns. This looks a bit nicer */}
                  {isProcessingNext && (
                    <div className='absolute bottom-0 left-0 right-0 top-32 z-50 flex items-center justify-center bg-white bg-opacity-75'>
                      <div className='text-center'>
                        <div className='mb-4'>
                          <div className='mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-purple-900'></div>
                        </div>
                        <div className='text-lg font-semibold'>Preparing table settings...</div>
                        <div className='text-gray-600'>Please wait while we optimize your configuration</div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit, state.values.csvConfig]}>
        {([canSubmit, csvConfig]) => (
          <StepButtons
            context={context}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            nextBlocked={!canSubmit || hasErrors || isProcessingNext || isInitializing}
            onNext={async () => {
              setIsProcessingNext(true)

              // starting when the next button is clicked, we now do the full validation
              // instead of each field having a constant running validator.
              const allColumnNames = form.getFieldValue('columnNames') || []
              const allColumnTypes = form.getFieldValue('columnTypes') || []

              // Quick validation without field-by-field updates
              const duplicates = new Map<string, number[]>()
              const sqlErrors: number[] = []

              // Check all names in a single pass
              for (let i = 0; i < allColumnNames.length; i++) {
                const name = allColumnNames[i]

                // Track duplicates
                if (duplicates.has(name)) {
                  duplicates.get(name)!.push(i)
                } else {
                  duplicates.set(name, [i])
                }

                // Check SQL compliance
                if (validateSqlColumnName(name)) {
                  sqlErrors.push(i)
                }
              }

              // Find actual duplicates (more than one occurrence)
              const duplicateIndices: number[] = []
              for (const [, indices] of duplicates) {
                if (indices.length > 1) {
                  duplicateIndices.push(...indices)
                }
              }

              // If there are errors, highlight them and stop
              if (duplicateIndices.length > 0 || sqlErrors.length > 0) {
                if (duplicateIndices.length > 0) {
                  notify.error(`Found ${duplicateIndices.length} duplicate column names. Please make them unique.`)
                }
                if (sqlErrors.length > 0) {
                  notify.error(
                    `Found ${sqlErrors.length} non-SQL compliant column names. Names must start with a letter or underscore and contain only letters, numbers, and underscores.`,
                  )
                }

                // Set all errors directly without promises
                const allErrorIndices = [...duplicateIndices.slice(0, 20), ...sqlErrors.slice(0, 20)]

                // Set errors for each field
                for (let i = 0; i < allErrorIndices.length; i++) {
                  const idx = allErrorIndices[i]
                  const fieldName = `columnNames.${idx}`
                  const errorMessage = duplicateIndices.includes(idx) ? 'Must be unique' : 'Must be SQL compliant'

                  // Try to set errors in a way that won't get overwritten
                  form.setFieldMeta(fieldName, (prev: any) => ({
                    ...prev,
                    errors: [errorMessage],
                    isDirty: true,
                    isBlurred: true,
                    isTouched: true,
                    isPristine: false,
                    isValidating: false,
                    errorMap: { ...prev?.errorMap, submit: errorMessage },
                    errorSourceMap: prev?.errorSourceMap || {},
                  }))
                }

                setTimeout(() => {
                  const currentFormState = form.store.state

                  // Try to scroll to first error
                  const firstErrorField = document.querySelector(`[name="columnNames.${allErrorIndices[0]}"]`)
                  if (firstErrorField) {
                    firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  }
                }, 100)

                setIsProcessingNext(false)
                return
              }

              // Keep only a small subset of data for the preview step (20 rows max)
              const previewData = parsedCsvData ? parsedCsvData.slice(0, 20) : null
              form.setFieldValue('parsedCsvData', previewData)
              form.setFieldValue('guessedColumnTypes', null)

              // Build columns object after clearing memory
              const columns: Record<string, string> = {}
              for (let i = 0; i < allColumnNames.length; i++) {
                columns[allColumnNames[i]] = allColumnTypes[i]
              }

              // Store config with minimal data. Don't carry forward too much because csv files with a large amount
              // of columns result in performance degradation
              const minimalConfig = {
                fileName: csvConfig?.fileName || csvFile.name,
                options: csvOptions,
                columns,
              }

              // Use requestAnimationFrame to let the browser update before heavy operation
              requestAnimationFrame(() => {
                form.setFieldValue('csvConfig', minimalConfig)
                // Small timeout to let state settle
                setTimeout(() => {
                  context.goToStep(StepId.TableSettings)
                }, 100)
              })
            }}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
